import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth-context";
import { WorkspaceProvider } from "@/lib/workspace-context";
import { EntitlementProvider } from "@/lib/entitlement-context";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import TrySample from "@/pages/TrySample";
import MarketingHome from "@/pages/marketing/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import ResumeAlignment from "@/pages/ResumeAlignment";
import InterviewPrep from "@/pages/InterviewPrep";
import InterviewPractice from "@/pages/InterviewPractice";
import VideoMockInterview from "@/pages/VideoMockInterview";
import MyAccount from "@/pages/MyAccount";
import LiveInterview from "@/pages/LiveInterview";
import Resumes from "@/pages/Resumes";
import History from "@/pages/History";
import InterviewBriefs from "@/pages/InterviewBriefs";
import InterviewPortfolio from "@/pages/InterviewPortfolio";
import Pricing from "@/pages/Pricing";
import PublicAssessment from "@/pages/PublicAssessment";
import SampleShare from "@/pages/SampleShare";
import UpgradeScreen from "@/pages/UpgradeScreen";
import PaymentReturn from "@/pages/PaymentReturn";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import { TrustOverview, PrivacyPolicy, TermsOfService, Security, CookiePolicy, AITransparency } from "@/pages/trust/TrustCenter";
import { Toaster } from "@/components/ui/sonner";

function InnerRouter() {
  const location = useLocation();
  // Synchronous check: if returning from OAuth (session_id in URL fragment), render AuthCallback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<MarketingHome />} />
      <Route path="/try" element={<TrySample />} />
      <Route path="/welcome-classic" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/trust" element={<TrustOverview />} />
      <Route path="/trust/privacy" element={<PrivacyPolicy />} />
      <Route path="/trust/terms" element={<TermsOfService />} />
      <Route path="/trust/security" element={<Security />} />
      <Route path="/trust/cookies" element={<CookiePolicy />} />
      <Route path="/trust/ai-transparency" element={<AITransparency />} />
      <Route path="/assessment/:shareId" element={<PublicAssessment />} />
      <Route path="/s/:shareId" element={<SampleShare />} />
      <Route path="/payment/success" element={<PaymentReturn mode="success" />} />
      <Route path="/payment/cancel" element={<PaymentReturn mode="cancel" />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Navigate to="/app/dashboard" replace />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout><Dashboard /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/upgrade"
        element={
          <ProtectedRoute>
            <UpgradeScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/analytics"
        element={
          <ProtectedRoute>
            <AppLayout><AdminAnalytics /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/workspaces"
        element={
          <ProtectedRoute>
            <AppLayout><InterviewPortfolio /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/resume"
        element={
          <ProtectedRoute>
            <AppLayout><ResumeAlignment /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/practice"
        element={
          <ProtectedRoute>
            <AppLayout><InterviewPractice /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/prep"
        element={
          <ProtectedRoute>
            <AppLayout><InterviewPrep /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/mock"
        element={<Navigate to="/app/practice" replace />}
      />
      <Route
        path="/app/profile"
        element={<Navigate to="/app/account/profile" replace />}
      />
      <Route
        path="/app/account/*"
        element={
          <ProtectedRoute>
            <AppLayout><MyAccount /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/video-mock"
        element={
          <ProtectedRoute>
            <AppLayout><VideoMockInterview /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/live"
        element={
          <ProtectedRoute>
            <AppLayout><LiveInterview /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/resumes"
        element={
          <ProtectedRoute>
            <AppLayout><Resumes /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/history"
        element={
          <ProtectedRoute>
            <AppLayout><History /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/briefs"
        element={
          <ProtectedRoute>
            <AppLayout><InterviewBriefs /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/billing"
        element={<Navigate to="/app/account/billing" replace />}
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <EntitlementProvider>
            <WorkspaceProvider>
              <InnerRouter />
              <Toaster position="bottom-right" />
            </WorkspaceProvider>
          </EntitlementProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
